import React, { useEffect } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { AppDispatch } from 'app/store';
import { ChakraTextareaField } from 'app/components/Core';
import { fieldValidationMessages } from 'app/constants';
import { Audio, AudioFormValidationProperties } from 'types/audio';
import { updateSongAudioEffect } from 'app/store/SongList/effects';
import { addAlertAction } from 'app/store/Alerts/slice';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { AudioFormItem } from '../AudioAddDialog/types';

interface AudioNoteDialogProps {
  open: boolean;
  handleClose: () => void;
  audioFormValidation: AudioFormValidationProperties;
  audio?: Audio;
  dispatch: AppDispatch;
}

const getValidationSchema = (
  audioFormValidation: AudioFormValidationProperties
) =>
  yup.object().shape({
    remark: yup
      .string()
      .max(
        audioFormValidation.remark.maxLength,
        fieldValidationMessages.MAX_CHAR(audioFormValidation.remark.maxLength)
      )
      .trim(),
  });

export const AudioNoteDialog = ({
  open,
  handleClose: closeDialog,
  audioFormValidation,
  audio,
  dispatch,
}: AudioNoteDialogProps) => {
  const {
    handleSubmit,
    register,
    reset,
    formState: { isSubmitting, isValid, isDirty, errors },
  } = useForm<Partial<AudioFormItem>>({
    defaultValues: { remark: audio?.remark || '' },
    mode: 'all',
    resolver: yupResolver(getValidationSchema(audioFormValidation)),
  });

  const handleClose = () => {
    closeDialog();
    reset();
  };

  const onSubmit = async (data: Partial<AudioFormItem>) => {
    try {
      if (audio) {
        await dispatch(
          updateSongAudioEffect({ audio, formData: data })
        ).unwrap();
        dispatch(addAlertAction({ message: 'Note saved' }));
      }
      handleClose();
    } catch (e) {
      // Handle error
    }
  };

  useEffect(() => {
    reset({ remark: audio?.remark || '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audio, reset]);

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      aria-labelledby="Audio note"
      aria-describedby="View audio note"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader data-testid="dialog-title" title="Note">
          Note
        </ModalHeader>
        <ModalBody data-testid="dialog-content">
          <ChakraTextareaField
            data-testid="audio-remark-field"
            label="Note"
            {...register('remark')}
            isInvalid={Boolean(errors.remark)}
            errorMessage={errors.remark?.message}
          />
        </ModalBody>
        <ModalFooter data-testid="dialog-actions">
          <Button onClick={handleClose} variant="secondary">
            Cancel
          </Button>
          <Button
            isDisabled={isSubmitting || !isValid || !isDirty}
            onClick={handleSubmit(onSubmit)}
            data-testid="AudioNoteDialog-save-btn"
            ml={2}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
